import { useEffect, useState } from "react";
import {
  Star,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Building2,
  User,
  MessageSquare,
  Send,
  X,
  Clock,
  Award,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Eye,
  ExternalLink,
  Briefcase
} from "lucide-react";
import EmptyState from "../../../../Components/EmptyState/EmptyState";
import { getMyApplications } from "../../../../services/applicationService";
import { submitEvaluation } from "../../../../services/evaluationService";
import { formatDatePtBr } from "../../../../utils/dateUtils";
import FreelancerViewCompanyModal from "../../../../Components/FreelancerViewCompanyModal/FreelancerViewCompanyModal";
import Toast from "../../../../Components/Toast/Toast"
import "./CompletedJobs.scss";

// ── Componente de Estrelas ──────────────────────────────────────────────
function StarRating({
  rating,
  interactive = false,
  onChange = null,
  size = 22,
  showLabel = false
}) {
  const [hover, setHover] = useState(0);

  const handleClick = (value) => {
    if (interactive && onChange) {
      onChange(value);
    }
  };

  const handleMouseEnter = (value) => {
    if (interactive) setHover(value);
  };

  const handleMouseLeave = () => {
    if (interactive) setHover(0);
  };

  return (
    <div className="cj-star-rating">
      {showLabel && (
        <span className="cj-star-rating__label">
          {rating > 0 ? `${rating} estrelas` : "Avaliar"}
        </span>
      )}
      <div className="cj-star-rating__stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`cj-star-rating__star ${star <= (hover || rating) ? "filled" : ""
              } ${interactive ? "interactive" : ""}`}
            size={size}
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onMouseLeave={handleMouseLeave}
          />
        ))}
      </div>
    </div>
  );
}

// ── Card de Trabalho Finalizado ─────────────────────────────────────────
function CompletedJobCard({
  item,
  onViewCompany,
  onStartEvaluation,
  onCancelEvaluation,
  onSubmitEvaluation,
  isEvaluating,
  ratingDraft,
  commentDraft,
  onRatingChange,
  onCommentChange,
  submittingEval
}) {
  const alreadyEvaluated = !!item.evaluation;

  return (
    <div className="cj-card">
      {/* Header */}
      <div className="cj-card__header">
        <div className="cj-card__company">
          {item.job.companyAvatar ? (
            <img
              src={item.job.companyAvatar}
              alt={item.job.companyName}
              className="cj-card__avatar"
            />
          ) : (
            <div className="cj-card__avatar-fallback">
              {item.job.companyName?.slice(0, 2).toUpperCase()}
            </div>
          )}

          <div className="cj-card__company-info">
            <button
              className="cj-card__company-link"
              onClick={() => onViewCompany(item.job.companyId)}
            >
              <Building2 size={14} />
              {item.job.companyName}
              <ExternalLink size={12} />
            </button>
            <h4 className="cj-card__title">{item.job.title}</h4>
          </div>
        </div>

        <div className="cj-card__status">
          {item.status === "completed" && (
            <span className="cj-card__tag cj-card__tag--completed">
              <CheckCircle size={14} />
              Concluído
            </span>
          )}
          {item.status === "early_exit" && (
            <span className="cj-card__tag cj-card__tag--early-exit">
              <AlertTriangle size={14} />
              Saída antecipada
            </span>
          )}
        </div>
      </div>

      {/* Job Info */}
      <div className="cj-card__info">
        <div className="cj-card__info-item">
          <Calendar size={14} />
          <span>{formatDatePtBr(item.job.date)}</span>
        </div>
        <div className="cj-card__info-item cj-card__info-item--payment">
          <DollarSign size={14} />
          <span>R$ {Number(item.job.payment).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Early Exit Reason */}
      {item.status === "early_exit" && item.earlyExitReason && (
        <div className="cj-card__exit-reason">
          <AlertTriangle size={14} />
          <span>Motivo: {item.earlyExitReason}</span>
        </div>
      )}

      {/* Evaluation Section */}
      <div className="cj-card__evaluation">
        {alreadyEvaluated ? (
          <div className="cj-card__evaluation-done">
            <div className="cj-card__evaluation-header">
              <ThumbsUp size={16} />
              <span>Sua avaliação:</span>
            </div>
            <StarRating rating={item.evaluation.rating} size={18} />
            {item.evaluation.comment && (
              <p className="cj-card__evaluation-comment">
                <MessageSquare size={14} />
                {item.evaluation.comment}
              </p>
            )}
          </div>
        ) : isEvaluating ? (
          <div className="cj-card__evaluation-form">
            <div className="cj-card__evaluation-header">
              <Award size={16} />
              <span>Avalie a empresa</span>
            </div>

            <StarRating
              rating={ratingDraft}
              interactive
              onChange={onRatingChange}
              size={28}
              showLabel
            />

            <textarea
              className="cj-card__evaluation-textarea"
              placeholder="Compartilhe sua experiência (opcional)"
              value={commentDraft}
              onChange={(e) => onCommentChange(e.target.value)}
              rows={3}
              maxLength={500}
            />

            <div className="cj-card__evaluation-actions">
              <button
                className="cj-card__evaluation-btn cj-card__evaluation-btn--cancel"
                onClick={onCancelEvaluation}
                disabled={submittingEval}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                className="cj-card__evaluation-btn cj-card__evaluation-btn--submit"
                onClick={() => onSubmitEvaluation(item)}
                disabled={submittingEval || ratingDraft === 0}
              >
                {submittingEval ? (
                  <>
                    <Loader2 size={16} className="cj-card__spinner" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Enviar avaliação
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <button
            className="cj-card__evaluation-trigger"
            onClick={() => onStartEvaluation(item)}
          >
            <Star size={16} />
            Avaliar Empresa
          </button>
        )}
      </div>
    </div>
  );
}

// ── Componente Principal ─────────────────────────────────────────────────
export default function CompletedJobs() {
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [ratingDraft, setRatingDraft] = useState(0);
  const [commentDraft, setCommentDraft] = useState("");
  const [submittingEval, setSubmittingEval] = useState(false);
  const [viewingCompanyId, setViewingCompanyId] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const freelancerId = localStorage.getItem("userId");

  const fetchCompleted = async () => {
    try {
      setLoading(true);
      const data = await getMyApplications();
      const finished = data.filter(
        (a) => a.status === "completed" || a.status === "early_exit"
      );
      setCompleted(finished);
    } catch (err) {
      console.error("Erro ao carregar trabalhos finalizados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompleted();
  }, []);

  const openEvaluation = (item) => {
    setEvaluatingId(item.id);
    setRatingDraft(0);
    setCommentDraft("");
  };

  const cancelEvaluation = () => {
    setEvaluatingId(null);
    setRatingDraft(0);
    setCommentDraft("");
  };

  const submitCompanyEvaluation = async (item) => {
    if (ratingDraft === 0) {
      showToast("Selecione uma nota de 1 a 5 estrelas.", "info");
      return;
    }

    setSubmittingEval(true);
    try {
      await submitEvaluation(item.id, ratingDraft, commentDraft.trim() || null);

      setCompleted((prev) =>
        prev.map((c) =>
          c.id === item.id
            ? {
              ...c,
              evaluation: {
                rating: ratingDraft,
                comment: commentDraft.trim() || null
              }
            }
            : c
        )
      );

      cancelEvaluation();
    } catch (err) {
      const data = err?.response?.data;
      const msg = typeof data === "string"
        ? data
        : data?.message || "Erro ao enviar avaliação.";
      showToast(msg, "error");
    } finally {
      setSubmittingEval(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="cj-loading">
        <Loader2 className="cj-loading__icon" />
        <span>Carregando trabalhos finalizados...</span>
      </div>
    );
  }

  // Empty
  if (completed.length === 0) {
    return (
      <div className="cj-empty">
        <EmptyState message="Você ainda não possui trabalhos finalizados." />
      </div>
    );
  }

  // Stats
  const totalEarnings = completed.reduce((sum, item) => sum + Number(item.job.payment), 0);
  const completedCount = completed.filter(item => item.status === "completed").length;
  const earlyExitCount = completed.filter(item => item.status === "early_exit").length;

  return (
    <section className="cj-wrapper">
      {/* Stats */}
      <div className="cj-stats">
        <div className="cj-stats__item">
          <Briefcase size={20} />
          <div>
            <span className="cj-stats__value">{completed.length}</span>
            <span className="cj-stats__label">Trabalhos</span>
          </div>
        </div>
        <div className="cj-stats__item">
          <CheckCircle size={20} />
          <div>
            <span className="cj-stats__value">{completedCount}</span>
            <span className="cj-stats__label">Concluídos</span>
          </div>
        </div>
        {earlyExitCount > 0 && (
          <div className="cj-stats__item cj-stats__item--warning">
            <AlertTriangle size={20} />
            <div>
              <span className="cj-stats__value">{earlyExitCount}</span>
              <span className="cj-stats__label">Saída antecipada</span>
            </div>
          </div>
        )}
        <div className="cj-stats__item cj-stats__item--highlight">
          <DollarSign size={20} />
          <div>
            <span className="cj-stats__value">
              R$ {totalEarnings.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </span>
            <span className="cj-stats__label">Total ganho</span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="cj-grid">
        {completed.map((item) => (
          <CompletedJobCard
            key={item.id}
            item={item}
            onViewCompany={setViewingCompanyId}
            onStartEvaluation={openEvaluation}
            onCancelEvaluation={cancelEvaluation}
            onSubmitEvaluation={submitCompanyEvaluation}
            isEvaluating={evaluatingId === item.id}
            ratingDraft={ratingDraft}
            commentDraft={commentDraft}
            onRatingChange={setRatingDraft}
            onCommentChange={setCommentDraft}
            submittingEval={submittingEval}
          />
        ))}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />

      {/* Modal */}
      {viewingCompanyId && (
        <FreelancerViewCompanyModal
          companyId={viewingCompanyId}
          onClose={() => setViewingCompanyId(null)}
        />
      )}
    </section>
  );
}